<script setup lang="ts">
import { Badge } from '~ds/components/ui/badge';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

interface Props {
  coinsEarned: number;
  totalCoins?: number | null;
  coinEffect?: 'none' | 'burst';
  isGhostUser?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  totalCoins: null,
  coinEffect: 'none',
  isGhostUser: false,
});

const { t } = useI18n();

const COIN_COUNT = 80;
const CONFETTI_COUNT = 44;

const showCoinEntry = ref(false);
const showExplode = ref(false);
const showBurst = ref(false);
const showCoinReturn = ref(false);
const showText = ref(false);
const showTotal = ref(false);
const totalPulse = ref(false);
const displayedCoins = ref(0);

const miniCoins = computed(() =>
  Array.from({ length: COIN_COUNT }, (_, i) => {
    const angle = (i / COIN_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 1.2;
    const distance = 60 + Math.random() * 280;
    const spin = (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 540);
    const wobbleX = (Math.random() - 0.5) * 80;
    const wobbleY = (Math.random() - 0.5) * 60;
    const duration = 1.6 + Math.random() * 1.0;
    return {
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      wobbleX,
      wobbleY,
      delay: Math.random() * 0.25,
      size: 10 + Math.random() * 24,
      spin,
      duration,
    };
  }),
);

const confetti = computed(() => {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA', '#34D399', '#FBBF24', '#F472B6'];
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => {
    const angle = (i / CONFETTI_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 1.2;
    const distance = 60 + Math.random() * 220;
    const duration = 1.4 + Math.random() * 0.8;
    return {
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      delay: Math.random() * 0.3,
      size: 5 + Math.random() * 8,
      color: colors[i % colors.length],
      spin: (Math.random() > 0.5 ? 1 : -1) * (200 + Math.random() * 600),
      duration,
    };
  });
});

const sparkles = computed(() =>
  Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2;
    const radius = 60 + Math.random() * 60;
    return {
      id: i,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      delay: i * 0.06,
      size: 5 + Math.random() * 8,
    };
  }),
);

const timers: ReturnType<typeof setTimeout>[] = [];
let rafId: number | null = null;

const countUp = (target: number, duration: number) => {
  const start = performance.now();
  const animate = (now: number) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - (1 - progress) ** 3;
    displayedCoins.value = Math.round(eased * target);
    if (progress < 1) {
      rafId = requestAnimationFrame(animate);
    }
  };
  rafId = requestAnimationFrame(animate);
};

onMounted(() => {
  if (props.coinEffect === 'none') {
    showCoinReturn.value = true;
    showText.value = true;
    showTotal.value = true;
    displayedCoins.value = props.coinsEarned;
    return;
  }

  timers.push(setTimeout(() => {
    showCoinEntry.value = true;
  }, 350));

  timers.push(setTimeout(() => {
    showExplode.value = true;
    showBurst.value = true;
  }, 900));

  timers.push(setTimeout(() => {
    showCoinEntry.value = false;
    showCoinReturn.value = true;
  }, 2100));

  timers.push(setTimeout(() => {
    showText.value = true;
    showTotal.value = true;
    countUp(props.coinsEarned, 900);
  }, 2600));

  timers.push(setTimeout(() => {
    totalPulse.value = true;
  }, 3500));
});

onUnmounted(() => {
  timers.forEach(clearTimeout);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
  }
});
</script>

<template>
  <div class="flex flex-col items-center gap-4">
    <motion.div
      class="text-center"
      :initial="{ opacity: 0, y: 20 }"
      :animate="{ opacity: 1, y: 0 }"
      :transition="{ duration: 0.4, ease: 'easeOut' }"
    >
      <Heading
        size="xl"
        as="h2"
        class="mb-1 leading-none"
      >
        {{ t('unified.activity.completion.wellDone') }}
      </Heading>
      <Text size="lg" class="font-semibold">
        {{ t('unified.activity.completion.youveEarned') }}
      </Text>
    </motion.div>

    <div class="relative flex flex-col items-center gap-3">
      <!-- Sparkle particles -->
      <div
        v-if="showBurst && coinEffect !== 'none'"
        class="pointer-events-none absolute inset-0 z-30 overflow-visible"
      >
        <div
          v-for="sparkle in sparkles"
          :key="`sparkle-${sparkle.id}`"
          class="coin-sparkle absolute left-1/2 top-1/4"
          :style="{
            '--sparkle-x': `${sparkle.x}px`,
            '--sparkle-y': `${sparkle.y}px`,
            '--sparkle-delay': `${sparkle.delay}s`,
            '--sparkle-size': `${sparkle.size}px`,
          }"
        />
      </div>

      <!-- Colored confetti -->
      <div
        v-if="showBurst && coinEffect !== 'none'"
        class="pointer-events-none absolute inset-0 z-20 overflow-visible"
      >
        <div
          v-for="piece in confetti"
          :key="`confetti-${piece.id}`"
          class="absolute left-1/2 top-1/4 confetti-arc-x"
          :style="{ '--conf-x': `${piece.x}px`, '--conf-delay': `${piece.delay}s`, '--conf-dur': `${piece.duration}s` }"
        >
          <div
            class="confetti-arc-y"
            :style="{ '--conf-y': `${piece.y}px`, '--conf-delay': `${piece.delay}s`, '--conf-dur': `${piece.duration}s` }"
          >
            <div
              class="confetti-spin rounded-sm"
              :style="{
                'width': `${piece.size}px`,
                'height': `${piece.size * 1.4}px`,
                'backgroundColor': piece.color,
                '--conf-spin': `${piece.spin}deg`,
                '--conf-delay': `${piece.delay}s`,
                '--conf-dur': `${piece.duration}s`,
              }"
            />
          </div>
        </div>
      </div>

      <!-- Burst coins (nested divs for smooth parabolic arcs) -->
      <div
        v-if="coinEffect !== 'none' && showBurst"
        class="pointer-events-none absolute inset-0 z-20 overflow-visible"
      >
        <div
          v-for="coin in miniCoins"
          :key="coin.id"
          class="absolute left-1/2 top-1/4 coin-arc-x"
          :style="{
            '--coin-x': `${coin.x}px`,
            '--coin-delay': `${coin.delay}s`,
            '--coin-dur': `${coin.duration}s`,
            '--coin-wobble-x': `${coin.wobbleX}px`,
          }"
        >
          <div
            class="coin-arc-y"
            :style="{
              '--coin-y': `${coin.y}px`,
              '--coin-delay': `${coin.delay}s`,
              '--coin-dur': `${coin.duration}s`,
              '--coin-wobble-y': `${coin.wobbleY}px`,
            }"
          >
            <div
              class="coin-spin"
              :style="{ '--coin-spin': `${coin.spin}deg`, '--coin-delay': `${coin.delay}s`, '--coin-dur': `${coin.duration}s` }"
            >
              <NuxtImg
                src="/images/kui/unified/gold-coin@2x.webp"
                alt=""
                aria-hidden="true"
                class="object-contain"
                :style="{ width: `${coin.size}px`, height: `${coin.size}px` }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Hero coin -->
      <div class="relative z-10">
        <!-- Entry: rises up, then explodes away -->
        <motion.div
          v-if="coinEffect === 'none'"
          :initial="{ opacity: 0, y: 40 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.5, ease: 'easeOut', delay: 0.1 }"
        >
          <NuxtImg
            src="/images/kui/unified/gold-coin@2x.webp"
            alt="Coins"
            class="size-28 sm:size-32 md:size-40"
          />
        </motion.div>

        <!-- Animated entry coin (appears → explodes) -->
        <div
          v-if="coinEffect !== 'none' && showCoinEntry && !showCoinReturn"
          :class="showExplode ? 'coin-explode' : 'coin-enter'"
        >
          <NuxtImg
            src="/images/kui/unified/gold-coin@2x.webp"
            alt="Coins"
            class="size-28 sm:size-36 md:size-44"
          />
        </div>

        <!-- Return: flips back in after burst -->
        <div
          v-if="coinEffect !== 'none' && showCoinReturn"
          class="coin-flip-in"
        >
          <NuxtImg
            src="/images/kui/unified/gold-coin@2x.webp"
            alt="Coins"
            class="size-28 sm:size-36 md:size-44"
          />
        </div>
      </div>

      <!-- Earned coins count -->
      <motion.div
        v-if="coinEffect === 'none' || showText"
        class="relative z-10 flex flex-col items-center gap-2"
        :class="{ 'rounded-3xl border-2 border-dashed border-foreground/40 px-5 py-3': isGhostUser }"
        :initial="{ opacity: 0, y: 12 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.35, ease: 'easeOut' }"
      >
        <Heading
          size="xl"
          as="h3"
          class="leading-none"
        >
          <template v-if="coinEffect !== 'none'">
            {{ displayedCoins.toLocaleString() }} {{ t('unified.activity.completion.coins') }}
          </template>
          <template v-else>
            {{ coinsEarned.toLocaleString() }} {{ t('unified.activity.completion.coins') }}
          </template>
        </Heading>
        <Badge
          v-if="isGhostUser"
          variant="soft"
          tone="yellow"
          rounded
          :uppercase="false"
          class="font-semibold"
          data-testid="completion-coins-unsaved"
        >
          <Icon
            :name="ICONS.lock"
            size="xs"
            class="shrink-0"
          />
          {{ t('unified.activity.completion.notSavedYet') }}
        </Badge>
      </motion.div>

      <!-- Why saving matters, where a signed-in student sees their running total -->
      <motion.div
        v-if="isGhostUser && (coinEffect === 'none' || showTotal)"
        class="relative z-10"
        :initial="{ opacity: 0, y: 8 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.35, ease: 'easeOut' }"
      >
        <Text
          size="sm"
          weight="medium"
          class="max-w-xs text-center"
          data-testid="completion-coins-ghost-hint"
        >
          {{ t('unified.activity.completion.ghostCoinsHint') }}
        </Text>
      </motion.div>

      <!-- Total coins badge -->
      <motion.div
        v-else-if="totalCoins && (coinEffect === 'none' || showTotal)"
        class="relative z-10"
        :class="{ 'total-pulse': totalPulse }"
        :initial="{ opacity: 0, scale: 0.8 }"
        :animate="{ opacity: 1, scale: 1 }"
        :transition="{ type: 'spring', stiffness: 150, damping: 15 }"
      >
        <div class="flex items-center gap-1.5 rounded-full bg-secondary-subtle px-3 py-1">
          <NuxtImg
            src="/images/kui/unified/gold-coins.webp"
            alt="Total coins"
            class="size-6"
          />
          <Text size="sm" class="font-semibold">
            {{ totalCoins.toLocaleString() }} {{ t('unified.activity.completion.totalCoins') }}
          </Text>
        </div>
      </motion.div>
    </div>
  </div>
</template>

<style scoped>
/* Phase 2: Coin rises in */
.coin-enter {
  animation: hero-enter 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes hero-enter {
  0% { opacity: 0; transform: translateY(80px) scale(0.5); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

/* Phase 3: Coin explodes — rapid scale down + flash */
.coin-explode {
  animation: hero-explode 0.25s cubic-bezier(0.55, 0, 1, 0.45) both;
}

@keyframes hero-explode {
  0% { opacity: 1; transform: scale(1); filter: brightness(1); }
  40% { opacity: 1; transform: scale(1.15); filter: brightness(1.8); }
  100% { opacity: 0; transform: scale(0.1); filter: brightness(2.5); }
}

/* Phase 4: Coin returns — flips while small, then scales up with spring */
.coin-flip-in {
  animation: hero-return 1.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes hero-return {
  /* Flip phase: coin spins while still small */
  0%   { opacity: 0; transform: scale(0.12); }
  6%   { opacity: 1; transform: scale(0.18) scaleX(0.1); }
  16%  { transform: scale(0.22) scaleX(-0.9); }
  26%  { transform: scale(0.28) scaleX(0.1); }
  36%  { transform: scale(0.32) scaleX(0.9); }
  /* Growth phase: scales up with spring bounce */
  60%  { transform: scale(1.08); }
  76%  { transform: scale(0.96); }
  88%  { transform: scale(1.02); }
  100% { opacity: 1; transform: scale(1); }
}

/* ── Sparkle four-point stars ── */
.coin-sparkle {
  width: var(--sparkle-size);
  height: var(--sparkle-size);
  animation: sparkle-twinkle 1.6s ease-in-out var(--sparkle-delay) both;
}

.coin-sparkle::before {
  content: '';
  display: block;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 223, 100, 1) 0%, rgba(255, 200, 50, 0.8) 30%, transparent 70%);
  clip-path: polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%);
}

@keyframes sparkle-twinkle {
  0% { opacity: 0; transform: translate(0, 0) scale(0) rotate(0deg); }
  25% { opacity: 1; transform: translate(calc(var(--sparkle-x) * 0.5), calc(var(--sparkle-y) * 0.5)) scale(1.3) rotate(60deg); }
  65% { opacity: 0.7; transform: translate(var(--sparkle-x), var(--sparkle-y)) scale(0.9) rotate(120deg); }
  100% { opacity: 0; transform: translate(calc(var(--sparkle-x) * 1.1), calc(var(--sparkle-y) * 1.1)) scale(0.2) rotate(180deg); }
}

/* ── Burst coins: split-axis with wobble for non-linear paths ── */
.coin-arc-x { animation: burst-x var(--coin-dur) ease-in-out var(--coin-delay) both; }
.coin-arc-y { animation: burst-y var(--coin-dur) ease-in var(--coin-delay) both; }
.coin-spin  { animation: burst-spin var(--coin-dur) linear var(--coin-delay) both; }

@keyframes burst-x {
  0%   { transform: translateX(0); opacity: 1; }
  20%  { transform: translateX(calc(var(--coin-x) * 0.7 + var(--coin-wobble-x))); opacity: 1; }
  45%  { transform: translateX(calc(var(--coin-x) + var(--coin-wobble-x) * -0.5)); opacity: 1; }
  70%  { transform: translateX(calc(var(--coin-x) * 0.3 + var(--coin-wobble-x) * 0.8)); opacity: 0.6; }
  100% { transform: translateX(0); opacity: 0; }
}

@keyframes burst-y {
  0%   { transform: translateY(0) scale(0.3); }
  20%  { transform: translateY(calc(var(--coin-y) * 0.6 + var(--coin-wobble-y))) scale(0.7); }
  45%  { transform: translateY(calc(var(--coin-y) + var(--coin-wobble-y) * -0.6)) scale(0.9); }
  70%  { transform: translateY(calc(var(--coin-y) * 0.5 + 80px)) scale(0.5); }
  100% { transform: translateY(220px) scale(0.12); }
}

@keyframes burst-spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(var(--coin-spin)); }
}

/* ── Confetti: same split-axis technique with per-piece duration ── */
.confetti-arc-x { animation: conf-x var(--conf-dur) ease-in-out var(--conf-delay) both; }
.confetti-arc-y { animation: conf-y var(--conf-dur) ease-in var(--conf-delay) both; }
.confetti-spin  { animation: conf-spin var(--conf-dur) linear var(--conf-delay) both; }

@keyframes conf-x {
  0%   { transform: translateX(0); opacity: 1; }
  30%  { transform: translateX(var(--conf-x)); opacity: 1; }
  100% { transform: translateX(calc(var(--conf-x) * 0.2)); opacity: 0; }
}

@keyframes conf-y {
  0%   { transform: translateY(0) scale(0.2); }
  30%  { transform: translateY(var(--conf-y)) scale(1); }
  100% { transform: translateY(260px) scale(0.5); }
}

@keyframes conf-spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(var(--conf-spin)); }
}

/* ── Total badge pulse ── */
.total-pulse {
  animation: badge-pulse 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes badge-pulse {
  0% { transform: scale(1); }
  40% { transform: scale(1.12); }
  100% { transform: scale(1); }
}
</style>
